import { useRef } from 'react'
import { useVisemeEnvelope } from '../useVisemeEnvelope'
import { useVizCanvas } from '../useVizCanvas'
import { withAlpha } from '../vizColor'

const RAYS = 48

// Radial spectrum + particle burst. Rays fan out from the center, each length
// driven by the envelope with a per-ray phase so the ring shimmers. On loud
// phonemes, particles are flung outward and fade. Energetic, sci-fi.
export function RadialViz({ character, color, anyVoice }) {
  const { read, activeRef, reduceMotion } = useVisemeEnvelope(character, { anyVoice })
  const lensRef = useRef(new Float32Array(RAYS))
  const partsRef = useRef([]) // {x,y,vx,vy,life}
  const prevRef = useRef(0)

  const draw = (ctx, { w, h, amp, t }) => {
    const cx = w / 2
    const cy = h / 2
    const inner = Math.min(w, h) * 0.14
    const maxLen = Math.min(w, h) * 0.34
    const lens = lensRef.current

    // Particle burst on a rising edge.
    if (amp - prevRef.current > 0.2) {
      const n = 10
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + t * 0.1
        const sp = 1.5 + amp * 3
        partsRef.current.push({
          x: cx + Math.cos(a) * inner,
          y: cy + Math.sin(a) * inner,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          life: 1,
        })
      }
    }
    prevRef.current = amp

    // Inner glow.
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, inner * 2.6)
    glow.addColorStop(0, withAlpha(color, 0.4 + amp * 0.4))
    glow.addColorStop(1, withAlpha(color, 0))
    ctx.fillStyle = glow
    ctx.beginPath()
    ctx.arc(cx, cy, inner * 2.6, 0, Math.PI * 2)
    ctx.fill()

    // Rays.
    ctx.lineCap = 'round'
    ctx.shadowColor = color
    ctx.shadowBlur = 6
    for (let i = 0; i < RAYS; i++) {
      const ang = (i / RAYS) * Math.PI * 2
      const wobble = 0.55 + 0.45 * Math.sin(t * 0.25 + i * 0.7)
      const target = amp * wobble
      lens[i] += (target - lens[i]) * (target > lens[i] ? 0.5 : 0.16)
      const len = inner + lens[i] * maxLen
      ctx.strokeStyle = withAlpha(color, 0.5 + lens[i] * 0.5)
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cx + Math.cos(ang) * inner, cy + Math.sin(ang) * inner)
      ctx.lineTo(cx + Math.cos(ang) * len, cy + Math.sin(ang) * len)
      ctx.stroke()
    }
    ctx.shadowBlur = 0

    // Particles.
    const parts = partsRef.current
    for (const p of parts) {
      p.x += p.vx
      p.y += p.vy
      p.vx *= 0.96
      p.vy *= 0.96
      p.life -= 0.03
    }
    partsRef.current = parts.filter((p) => p.life > 0)
    for (const p of partsRef.current) {
      ctx.fillStyle = withAlpha(color, p.life)
      ctx.beginPath()
      ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  const ref = useVizCanvas({ read, activeRef, reduceMotion, draw })
  return <canvas ref={ref} style={{ display: 'block', width: '100%', height: '100%' }} />
}
